
import React, { useState, useRef, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { DFPAPIEndpointGalley } from '../../../utils/axios';
import moment from  'moment';
import {
  SeatCellRenderer,
  NameCellRenderer,
  PNRCellRenderer,
  ClassCellRenderer,
  NationalityCellRenderer,
  ContactCellRenderer,
  SSRCellRenderer,
  StatusCellRenderer,
  CLASS_COLORS,
  CLASS_COLOR_FALLBACK
} from './PaxManifest-renderers';
import { SSR_CODES } from '../../../utils/configs';
import { formatDateByTimezone } from '../../../utils/commonFunctions';
 
let getPassengerColumns;
try {
  const config = require('../../../utils/configs');
  getPassengerColumns = config.getPassengerColumns;
   
} catch (e) {

getPassengerColumns = () => [
  { 
    field: 'seatNo', 
    headerName: 'Seat No', 
    minWidth: 60, 
    maxWidth: 70, 
    flex: 1, 
    suppressSizeToFit: true, 
    resizable: false   
  },
  { 
    field: 'name', 
    headerName: 'Passenger Name', 
    flex: 1, 
    suppressSizeToFit: true, 
    resizable: true,   
    minWidth: 200,
    width: 250,
    maxWidth:600,
    tooltipField: 'name',  
    cellStyle: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }
  },
  { 
    field: 'pnr', 
    headerName: 'PNR', 
    minWidth: 100,
    resizable: false 
  },
  { 
    field: 'class', 
    headerName: 'Class', 
    minWidth: 80,
    resizable: false 
  },
  { 
    field: 'nationality', 
    headerName: 'Nationality', 
    minWidth: 80,   
    resizable: false 
  },
  { 
    field: 'contact', 
    headerName: 'Contact', 
    minWidth: 120,
    resizable: false 
  },
  { 
    field: 'ssrs', 
    headerName: 'SSRs', 
    minWidth: 100,
    resizable: false 
  },
  { 
    field: 'status', 
    headerName: 'Status', 
    minWidth: 100,
    resizable: false 
  }
];
}

    const CLASS_LABELS = {
  'EC': 'Economy',
  'CP': 'Premium Economy',
  'AV': 'Aviation',
  'BU': 'Business Class',
  'F': 'First Class'
};


export const PaxManifest = ({ sector}) => {
  const [selectedManifest, setSelectedManifest] = useState(null);
  const [passengers, setPassengers] = useState({ count: 0, items: [] });
  const [filterSSRCode, setFilterSSRCode] = useState('');
  const [filterStatus, setFilterStatus] = useState('');  
  const [filterClass, setFilterClass] = useState('');  
  const [searchText, setSearchText] = useState('');  
const [filterEmptySeats, setFilterEmptySeats] = useState(false);  
  const gridRef = useRef(null);

  const sectorData = sector;

  const passengerColumns = useMemo(() => {
    try {
 
      const baseCols = typeof getPassengerColumns === 'function' 
        ? getPassengerColumns() 
        : getPassengerColumns;
      
      if (!baseCols || !Array.isArray(baseCols)) {
       
        return [];
      }
 
      const baseColumns = baseCols.map((col) => {
        if (!col || !col.field) {
        
          return null;
        }

       const baseCol = {
        field: col.field || '',
        headerName: col.headerName || col.field || '',
        resizable: col.resizable !== undefined ? col.resizable : true,  
        suppressSizeToFit: col.suppressSizeToFit !== undefined ? col.suppressSizeToFit : true,
        minWidth: col.minWidth || 100,
        flex: col.flex || 1   
        };

        switch (col.field) {
          case 'seatNo':
            return { ...baseCol, cellRendererFramework: SeatCellRenderer };
          case 'name':
            return { ...baseCol, cellRendererFramework: NameCellRenderer };
          case 'pnr':
            return { ...baseCol, cellRendererFramework: PNRCellRenderer };
          case 'class':
            return { ...baseCol, cellRendererFramework: ClassCellRenderer };
          case 'nationality':
            return { ...baseCol, cellRendererFramework: NationalityCellRenderer };
          case 'contact':
            return { ...baseCol, cellRendererFramework: ContactCellRenderer };
          case 'ssrs':
            return { ...baseCol, cellRendererFramework: SSRCellRenderer };
          case 'status':
            return { ...baseCol, cellRendererFramework: StatusCellRenderer };
          default:
            return baseCol;
        }
      }).filter(col => col !== null);
 
      return baseColumns;
    } catch (error) {
    
      return [];
    }
  }, []);

  const fetchPassengerManifest = async () => {
    try {
      if (!sectorData) {
      
        setPassengers({ count: 0, items: [] });
        return { count: 0, items: [] };
      }

      console.log('🔍 Fetching manifest for:', sectorData);

      if (gridRef.current?.api) {
        gridRef.current.api.showLoadingOverlay();
      }

      const response = await DFPAPIEndpointGalley.post(
        'manifest/api/v1/getManifests',
        {
          sectors: [
            {
              flightNumber: sectorData.flightNumber.split('-')[0] || '',
              origin: sectorData.origin || '',
              destination: sectorData.destination || '',
              flightDate: formatDateByTimezone(sectorData?.flightDate)
            }
          ]
        }
      );

    

      if (response?.manifests && Array.isArray(response.manifests)) {
        const manifest = response.manifests[0];

        if (manifest) {
          setSelectedManifest(manifest);
          const passengerData = manifest?.passengers || [];
       
          const passengerState = {
            count: passengerData.length,
            items: passengerData
          };
          setPassengers(passengerState);

          if (gridRef.current?.api) {
            gridRef.current.api.hideOverlay();
          }
 

          return passengerState;
        }
      }

      if (gridRef.current?.api) {
        gridRef.current.api.showNoRowsOverlay();
      }

      const emptyState = { count: 0, items: [] };
      setPassengers(emptyState);
 

      return emptyState;
    } catch (error) {
      
      if (gridRef.current?.api) {
        gridRef.current.api.showNoRowsOverlay();
      }

      const emptyState = { count: 0, items: [] };
      setPassengers(emptyState);
      return emptyState;
    }
  };

  React.useEffect(() => {
    if (sectorData) {
      fetchPassengerManifest();
    }
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectorData?.flightNumber, sectorData?.origin, sectorData?.destination]);

 
  const filteredPassengers = useMemo(() => {
    let filtered = passengers.items || [];
    if (filterSSRCode) {
      filtered = filtered.filter(p =>
        p.ssrs && p.ssrs.some(ssr => (ssr.ssrCode || ssr.code) === filterSSRCode)
      );
    }

    if (filterStatus) {
      filtered = filtered.filter(p => String(p.status) === String(filterStatus));
    }

    if (filterClass) {
      filtered = filtered.filter(p => p.class === filterClass);
    }

    if (searchText.trim()) {
      filtered = filtered.filter(p =>
        (p.name && p.name.toLowerCase().includes(searchText.toLowerCase())) ||
        (p.seatNo && p.seatNo.toLowerCase().includes(searchText.toLowerCase())) || 
        (p.pnr && p.pnr.toLowerCase().includes(searchText.toLowerCase()))
      );
    }
 
    if (filterEmptySeats) {
      filtered = filtered.filter(p => !p.seatNo || p.seatNo.trim() === '');
    }

  filtered.sort((a, b) => {
      const seatA = a.seatNo ? a.seatNo.trim() : '';
      const seatB = b.seatNo ? b.seatNo.trim() : '';

      if (!seatA && !seatB) return 0;
      if (!seatA) return 1; 
      if (!seatB) return -1;

      const matchA = seatA.match(/^(\d+)([A-Z]?)$/);
      const matchB = seatB.match(/^(\d+)([A-Z]?)$/);

      if (!matchA || !matchB) {
        return seatA.localeCompare(seatB);  
      }

      const numA = parseInt(matchA[1], 10);
      const numB = parseInt(matchB[1], 10);
      const letterA = matchA[2] || '';
      const letterB = matchB[2] || '';

      if (numA !== numB) {
        return numA - numB;
      }

      return letterA.localeCompare(letterB);
    });
    return filtered;
  }, [passengers.items, filterSSRCode, filterStatus, filterClass, searchText, filterEmptySeats]);

  const calculateStats = (passengerState) => {
    const passengerList = passengerState?.items || [];
    const classCounts = passengerList.reduce((acc, p) => {
      if (p.class) acc[p.class] = (acc[p.class] || 0) + 1;
      return acc;
    }, {});
    return {
      total: Object.values(classCounts).reduce((sum, n) => sum + n, 0),
      classCounts,
    
      totalSSR: passengerList.reduce((sum, p) => sum + (p.ssrs?.length || 0), 0),
      
    };
  };

const uniqueClasses = useMemo(() => {
  const classes = new Set();
  (passengers.items || []).forEach(p => {
    if (p.class) {
      classes.add(p.class);
    }
  });
  return Array.from(classes).sort();
}, [passengers.items]);

  const stats = calculateStats(passengers);

   const uniqueSSRCodes = useMemo(() => {
    const codes = new Set();
    (passengers.items || []).forEach(p => {
      if (p.ssrs && Array.isArray(p.ssrs)) {
        p.ssrs.forEach(ssr => {
          const code = ssr.ssrCode || ssr.code || '';
          if (code) codes.add(code);
        });
      }
    });
    return Array.from(codes).sort();
  }, [passengers.items]);


  if (!sectorData) {
    return (
      <div
        style={{
          padding: '2rem',
          textAlign: 'center',
          color: '#3d5a7a',
          background: 'rgba(40, 80, 140, 0.15)',
          borderRadius: '8px',
          border: '1px dashed rgba(40, 80, 140, 0.3)',
          fontFamily: "'Inter', sans-serif"
        }}
      >
        <p style={{ margin: 0, fontSize: '1rem' }}>
          No sector data available. Please select a flight to view passenger manifest.
        </p>
      </div>
    );
  }

  return (
<>
    <style>{`
  select option {
    background-color: #0c1526;
    color: #c8e0e6;
    padding: 0.5rem;
  }
  
  select option:hover {
    background-color: rgba(76, 195, 230, 0.2);
    color: #133742;
  }
  
  select option:checked {
    background: linear-gradient(#0c1526, #0c1526);
    background-color: rgba(76, 195, 230, 0.3);
    color: #0c1526;
  }
`}</style>

<div
      className="pax-manifest-container"
      style={{
        background: '#0c1526',
        color: '#d6eaf8',
        padding: '2rem 2rem 0.5rem',
        borderRadius: '12px',
        display: 'flex',
        flexDirection: 'column',
        gap: '1.5rem',
        height: '100%'
      }}
    >

  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: '2rem',
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' , background:'#1b2b41', border:'1px solid #1d3f4f', padding:'0.5rem', borderRadius:'5px' }}>
      <h1
        style={{
          fontSize: '2.5rem',
          fontWeight: '700',
          color: '#4cc3e6',
          margin: 0
        }}
      >
        {selectedManifest?.flightNumber || sectorData.flightNumber}
      </h1>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
        <span
          style={{
            background: 'rgba(76, 195, 230, 0.15)',
            border: '1px solid rgba(76, 195, 230, 0.3)',
            padding: '0.35rem 0.75rem',
            borderRadius: '6px',
            color: '#d6eaf8',
            fontSize: '0.9rem',
            fontWeight: '600'
          }}
        >
          {sectorData.origin}
        </span>
        <span style={{ color: '#4cc3e6', fontSize: '1rem' }}>→</span>
        <span
          style={{
            background: 'rgba(76, 195, 230, 0.15)',
            border: '1px solid rgba(76, 195, 230, 0.3)',
            padding: '0.35rem 0.75rem',
            borderRadius: '6px',
            color: '#d6eaf8',
            fontSize: '0.9rem',
            fontWeight: '600'
          }}
        >
          {sectorData.destination}
        </span>
        <span style={{ color: '#8aaacf', fontSize: '0.85rem', marginLeft: '0.5rem' }}>
          {moment(formatDateByTimezone(new Date(sectorData.flightDate)))
                  .format('DD MMM YYYY')}
        </span>
      </div>
    </div>
 
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
        gap: '0.8rem',
        alignItems: 'center',
        flex: 1
      }}
    >
      {[
        { label: 'Occupied', value: stats.total },
        ...Object.keys(stats.classCounts).sort().map((cls) => ({
          label: CLASS_LABELS[cls] || cls,
          value: stats.classCounts[cls],
          colors: CLASS_COLORS[cls] || CLASS_COLOR_FALLBACK
        })),
 
        { label: 'Total SSRs', value: stats.totalSSR },
 
      ].map((stat, idx) => (
        <div
          key={idx}
          style={{
            background: stat.colors ? stat.colors.bg : 'rgba(76, 195, 230, 0.08)',
            border: `1px solid ${stat.colors ? stat.colors.border : 'rgba(40, 80, 140, 0.45)'}`,
            padding: '0.4rem 0.8rem',
            borderRadius: '6px',
            minWidth: 'fit-content'
          }}
        >
          <span style={{ fontSize: '0.8rem', color: '#a6bed7ff', marginRight: '0.35rem' }}>
            {stat.label}:
          </span>
          <span style={{ fontSize: '1.1rem', fontWeight: '700', color: stat.colors ? stat.colors.text : '#4cc3e6' }}>
            {stat.value}
          </span>
        </div>
      ))}
    </div>
  </div>
<div style={{   borderBottom: '1px solid rgba(40, 80, 140, 0.45)'}}></div>

  {/* Search Bar */}
  <div style={{
  display: 'flex',
  gap: '1rem',
  justifyContent:'space-between'
  }}>

  <div
  style={{
  display: 'flex',
  justifyContent: 'flex-start',
  gap: '0.8rem'
  }}
  >
    <input
      type="text"
      placeholder="Search by name or seat number or pnr..."
      value={searchText}
      onChange={(e) => setSearchText(e.target.value)}
      style={{
        background: 'rgba(76, 195, 230, 0.1)',
        border: '1px solid rgba(76, 195, 230, 0.3)',
        color: '#f7fdfeff',
        padding: '0.5rem 0.8rem',
        borderRadius: '4px',
        fontSize: '0.85rem',
        width: '350px',
        maxWidth: '400px',
        fontFamily: 'inherit',
        outline: 'none'
      }}
    />

    <label style={{
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      cursor: 'pointer',
      fontSize: '0.8rem',  
      whiteSpace: 'nowrap',
      padding: '0.4rem 0.6rem',
      borderRadius: '4px',
      color: 'rgb(166, 190, 215)'
    }}>
      <input
        type="checkbox"
        checked={filterEmptySeats}
        onChange={(e) => setFilterEmptySeats(e.target.checked)}
        style={{
          cursor: 'pointer',
          width: '16px',
          height: '16px',
          accentColor: '#4cc3e6'   
        }}
      />
      Show Empty Seat No
    </label>

  </div>


  <div
  style={{
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '0.8rem'
  }}
  >
  {/* Class Filter */}
  <select
  value={filterClass}
  onChange={(e) => setFilterClass(e.target.value)}
  style={{
    background: 'rgba(76, 195, 230, 0.1)',
    border: '1px solid rgba(76, 195, 230, 0.3)',
    color: '#7dd8ef',
    padding: '0.35rem 0.6rem',
    borderRadius: '4px',
    fontSize: '0.8rem',
    cursor: 'pointer'
  }}
>
  <option value="">Select Class</option>
  {uniqueClasses.map(classCode => (
    <option key={classCode} value={classCode}>
      {CLASS_LABELS[classCode] || classCode}
    </option>
  ))}
</select>





  <select
  value={filterSSRCode} 
  onChange={(e) => setFilterSSRCode(e.target.value)} 
  style={{
  background: 'rgba(76, 195, 230, 0.1)',
  border: '1px solid rgba(76, 195, 230, 0.3)',
  color: '#7dd8ef',
  padding: '0.35rem 0.6rem',
  borderRadius: '4px',
  fontSize: '0.8rem',
  cursor: 'pointer'
  }}
  >
  <option value=""> Select SSR Code</option>
  {uniqueSSRCodes.map(code => (  
  <option key={code} value={code}>
  {code} - {SSR_CODES[code] || code}
  </option>
  ))}
  </select>
  <select
  value={filterStatus}
  onChange={(e) => setFilterStatus(e.target.value)}
  style={{
  background: 'rgba(76, 195, 230, 0.1)',
  border: '1px solid rgba(76, 195, 230, 0.3)',
  color: '#7dd8ef',
  padding: '0.35rem 0.6rem',
  borderRadius: '4px',
  fontSize: '0.8rem',
  cursor: 'pointer'
  }}
  >
  <option value="">Select Status</option>
  <option value="1">Checked In</option>
  <option value="2">Boarded</option>
  <option value="3">Cancelled</option>
  <option value="0">No Show</option>
  </select>

  </div>


  </div>
 
  {/* <div
    style={{
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '0.8rem'
    }}
  >

  </div> */}


      {/* ===== AG-GRID SECTION ===== */}
       <div className="table-header"></div>
      <div
        id="pax-manifest-grid"
        className="ag-theme-alpine-dark"
        style={{
          flex: 1,
          minHeight: 0,
          marginTop: '-24px' ,
          width: '100%',
          userSelect:'text'
        }}
      >
        <AgGridReact
          ref={gridRef}
          rowData={filteredPassengers}  
          columnDefs={passengerColumns && passengerColumns.length > 0 ? passengerColumns : []}
          className="ag-theme-alpine-dark no-wrap"
          suppressCellFocus={true}
          overlayLoadingTemplate={
            '<span class="ag-overlay-loading-center">Loading passengers...</span>'
          }
          overlayNoRowsTemplate={
            '<span class="ag-overlay-no-rows-center">No Passengers Found</span>'
          }
          getRowStyle={() => ({
            borderBottom: '1px solid rgba(40, 80, 140, 0.3)'
          })}
          onGridReady={(params) => {
            if (filteredPassengers?.length > 0) {
              params.api.hideOverlay();
            } else if (filteredPassengers?.length === 0 ) {
              params.api.showNoRowsOverlay();
            }
          }}
          getRowHeight={(params) => {
            const ssrCount = params.data?.ssrs?.length || 0;
            if (ssrCount >= 4) return 100;   
            if (ssrCount >= 2) return 80;     
            return 50;                     
          }}
          getRowClass={(params) => {
            const classes = [];
            if (params.node.rowIndex % 2 === 0) {
              classes.push('ag-row-even');
            }
            return classes.join(' ');
          }}
          defaultColDef={{
            resizable: true,
            sortable: true,
            filter: true
          }}
        />
      </div>
    </div>
</>


    
  );
};